import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidV4 } from 'uuid';
import { concatClassNames } from '../../utils';
import { SpinnerIcon } from '../icons';

export interface ICheckboxProps {
  id?: string;
  label?: string;
  labelWrapperClassName?: string;
  description?: string | React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  inputWrapperClassName?: string;
  error?: string;
  placeholder?: string;
  loading?: boolean;
  extraInfo?: string;
  additionalRequiredLabel?: boolean;
  forceShowingDescription?: boolean;
}

type TInputType = ICheckboxProps & React.ComponentProps<'input'>;

/**
 * @deprecated This component is deprecated and should not be used in new code.
 * Please migrate to the new component.
 * @link src/lib/Checkbox/Checkbox.tsx
 */
const Checkbox: FunctionComponent<TInputType> = ({
  id,
  name,
  label,
  labelWrapperClassName,
  className,
  wrapperClassName,
  inputWrapperClassName,
  error,
  placeholder,
  extraInfo,
  description,
  loading,
  additionalRequiredLabel,
  forceShowingDescription,
  ...rest
}) => {
  const { t } = useTranslation('checkbox');

  let inputStyle = 'focus:ring-blue-500 h-5 w-5 border-gray-300 rounded mt-1';

  if (error) {
    inputStyle = concatClassNames(inputStyle, 'border-red-500 hover:border-red-500 focus:border-red-500 outline-none');
  }

  if (rest.disabled) {
    inputStyle = concatClassNames(inputStyle, 'mt-2 bg-tertiary-background text-brand-400');
  } else {
    inputStyle = concatClassNames(inputStyle, 'mt-2 text-brand-600');
  }

  if (!id) {
    id = uuidV4();
  }

  return (
    <div className={twMerge('inline-flex flex-col w-full my-2 text-left', wrapperClassName)}>
      <div className='relative flex items-start'>
        <div className={twMerge('flex items-center h-5', inputWrapperClassName)}>
          <input
            {...rest}
            id={id}
            name={name}
            type='checkbox'
            placeholder={placeholder}
            className={className ? concatClassNames(inputStyle, className) : inputStyle}
          />
        </div>
        {label || description ? (
          <div className='ml-3 text-base'>
            {label && (
              <label htmlFor={id} className={twMerge('block font-medium text-gray-700 cursor-pointer', labelWrapperClassName)}>
                {label}
                {additionalRequiredLabel && rest.required && <sub> ({t('required')})</sub>}
              </label>
            )}
            <p id='checkbox-description' className={`font-normal text-gray-500 ${forceShowingDescription && !description ? 'h-6' : ''}`}>
              {description}
            </p>
          </div>
        ) : null}
        {loading && (
          <div className='ml-4 flex h-6 justify-center text-center'>
            <SpinnerIcon className='size-4' fill='rgba(21,68,155,1)' loading />
          </div>
        )}
      </div>
      {extraInfo && <p className='mt-2 text-sm text-gray-800'>{extraInfo}</p>}
      {error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
    </div>
  );
};

export default Checkbox;
